/**
 * THE CLINICAL TRIAL PHASE, read only where the protocol states it AS a trial
 * phase (backlog e4, 2026-10-21).
 *
 * Found on the Penn digital-health study (NCT05277194; ClinicalTrials.gov
 * phase "Not Applicable"): the protocol runs in project PHASES ("Phase 1
 * representing contextual inquiry", "Phase 2 representing ... Usability
 * Testing", "Phase 3 representing the Pilot Trial"), and its IRB application
 * has a "Phase*" field answered "Phase I". The protocol analysis recorded
 * "Phase 1", and the DMP, DRCP, DBLC, MCS and SAERP called it "an open-label,
 * randomized, Phase 1 interventional study". There is no drug in it.
 *
 * What counts as a statement of the trial phase (everything else is ignored):
 *   - a FIELD: a "Phase" / "Study Phase" / "Trial Phase" / "Clinical Phase" /
 *     "Phase of Development" label followed by the value, after a colon on the
 *     same line or alone on the next line ("Phase*\nPhase I",
 *     "Phase: Phase 3", "Phase of Development: II", "Phase: Not applicable");
 *   - a STATEMENT: the phase directly followed by a study or trial word, with
 *     only design adjectives between ("A Phase 3, Randomized, Double-blind
 *     Study", "phase IIb clinical trial"), either near the start of the
 *     document (title page, synopsis) or introduced as THIS study ("This is a
 *     Phase 2b ...", "the present Phase 1 trial"). A Phase 3 trial cited in
 *     the background ("A previous Phase 3 trial showed ...") is not this
 *     study's phase.
 * Never counted: a heading or stage ("Phase 1: Contextual Inquiry", "During
 * Phase 2 participants ...", "Phase 1.2", "Phase 3 (Pilot Trial)", "Phase 3
 * pilot trial participants", "Phase 1 of recruitment").
 *
 * Same discipline as saeHoursFromProtocol.ts: prefer nothing to a guess. Two
 * different stated phases are a conflict, returned as such and never resolved
 * by picking one.
 *
 * Pure.
 */
import type { OperationsFact } from "./operationsFromProtocol";
import type { FactSource, ProductType } from "./types";

export const NOT_APPLICABLE_PHASE = "Not applicable";
/** ClinicalTrials.gov's "Early Phase 1" (formerly Phase 0); CDISC TPHASE "EARLY PHASE I". */
export const EARLY_PHASE_1 = "Early Phase 1";

/**
 * Words before a phase statement, in the same sentence, that make it a
 * citation of ANOTHER trial ("Smith et al. reported a Phase 3 trial", "Y was
 * approved following a large Phase 3 trial", "A previous Phase 3 trial").
 * Checked in the title region too (checker, round 4).
 */
const CITED = /(?<![\p{L}\p{N}])(?:prior|previous(?:ly)?|earlier|former|preceding|reported|approved|published|completed|et\s+al)(?![\p{L}\p{N}])/iu;

/** The sentence up to the index: from the last line break or sentence end ("et al." is not one). */
function sentenceBefore(text: string, index: number): string {
  const s = text.slice(Math.max(0, index - 200), index);
  let cut = s.lastIndexOf("\n");
  // A colon ends the clause too: "PUBLISHED PROTOCOL: A Phase 3 ... Trial" (round 5).
  for (const m of s.matchAll(/(?<!\bal)[.;:!?]\s/g)) cut = Math.max(cut, (m.index ?? 0) + m[0].length - 1);
  return s.slice(cut + 1);
}

/** Text placeholders that are not a phase ("Not specified.", "To be determined"). */
export const PHASE_PLACEHOLDER = /^(?:not\s+(?:specified|stated|known|reported|available|determined|yet\s+determined)|unspecified|unknown|to\s+be\s+(?:determined|confirmed|decided|defined)|tbd|tbc|tba|pending|none)\.?$/i;

/** A phase numeral: Roman upper-case only (so "phase is" and "i.e." never read as Phase I), or 0-4, with an optional a/b. */
const NUM = "(?:IV|III|II|I|[0-4])(?:[abAB])?";
/**
 * One phase or a combined one ("1/2", "I/II", "1b/2", "2-3", and the registry form
 * "1/Phase 2"); never a decimal stage ("1.2").
 */
const PHASE_VALUE = `(${NUM}(?:\\s*[/-]\\s*(?:(?:[Pp]hase|PHASE)[ \\t-]*)?${NUM})?)(?![\\p{L}\\p{N}])(?!\\.\\d)`;
const ROMAN: Record<string, string> = { I: "1", II: "2", III: "3", IV: "4" };

/**
 * Design adjectives that may stand between the phase and "study" / "trial".
 * "pilot" is deliberately absent: Penn's "Phase 3 pilot trial participants"
 * names a project stage, and a pilot study that is a real trial phase states
 * its phase elsewhere (title, synopsis field).
 */
const DESIGN = `(?:${[
  "randomi[sSzZ]ed", "non-?randomi[sSzZ]ed", "double[- ]?blind(?:ed)?", "single[- ]?blind(?:ed)?", "triple[- ]?blind(?:ed)?",
  "observer[- ]?blind(?:ed)?", "open[- ]?label(?:led)?", "placebo[- ]?controlled", "active[- ]?controlled",
  "vehicle[- ]?controlled", "sham[- ]?controlled", "controlled", "uncontrolled", "multi-?cent(?:er|re|ric)",
  "single[- ]?cent(?:er|re)", "multi-?national", "international", "global", "multiple[- ]?dose", "single[- ]?dose",
  "dose[- ]?escalation", "dose[- ]?finding", "dose[- ]?ranging", "first[- ]?in[- ]?human", "parallel(?:[- ]?(?:group|arm))?",
  "cross-?over", "prospective", "interventional", "adaptive", "seamless", "pivotal", "confirmatory", "registrational",
  "safety", "efficacy", "pharmacokinetic", "non-?inferiority", "superiority", "two[- ]?part", "two[- ]?stage",
  "single[- ]?arm", "two[- ]?arm", "extension", "expansion", "post[- ]?marketing", "observational", "micro[- ]?dosing", "and",
].map(caseless).join("|")})`;

/** A pattern made case-insensitive letter by letter, leaving character classes and groups alone. */
function caseless(pattern: string): string {
  let out = "";
  let inClass = false;
  for (let i = 0; i < pattern.length; i++) {
    const c = pattern[i];
    if (c === "\\") { out += c + (pattern[i + 1] ?? ""); i++; continue; }
    if (c === "[") inClass = true;
    if (c === "]") inClass = false;
    out += !inClass && /[a-z]/i.test(c) ? `[${c.toLowerCase()}${c.toUpperCase()}]` : c;
  }
  return out;
}

const PHASE_WORD = "(?:[Pp]hase|PHASE)";
const STUDY_WORD = caseless("(?:clinical\\s+)?(?:study|trial|investigation)");
/** "Phase 3, Randomized, Double-blind Study": the phase, design words only, then the study word. */
const STATEMENT = new RegExp(
  `(?<![\\p{L}\\p{N}])${PHASE_WORD}[ \\t-]*${PHASE_VALUE}(?:[\\s,]+${DESIGN}(?![\\p{L}\\p{N}]))*[\\s,]+${STUDY_WORD}(?![\\p{L}\\p{N}])`,
  "gu",
);
/** "This is a Phase 2b ...", "the present Phase 1 trial", "This randomized Phase 3 study". */
const THIS_STUDY = new RegExp(
  `${caseless("(?:this|the\\s+(?:present|current|proposed))(?:\\s+(?:study|trial))?(?:\\s+(?:is|will\\s+be))?(?:\\s+an?)?")}(?:[\\s,]+${DESIGN})*[\\s,]+$`,
  "u",
);
/** Characters of the document treated as its title page and synopsis. */
export const TITLE_REGION_CHARS = 4000;

const LABEL = `(?:${caseless("(?:study|trial|clinical|development(?:al)?)")}\\s+)?${PHASE_WORD}(?:\\s+${caseless("of\\s+(?:clinical\\s+)?development")})?`;
/** Group 1: a phase; group 2: not applicable; group 3: early phase 1 ("Early Phase 1", ClinicalTrials.gov's term for Phase 0). */
const FIELD_VALUE = `(?:${PHASE_WORD}[ \\t-]*)?(?:${PHASE_VALUE}|(${caseless("N\\/?A|not\\s+applicable")})|(${caseless("early")}[ \\t]+${PHASE_WORD}[ \\t-]*(?:1|I)(?![\\p{L}\\p{N}])))`;
/** "Phase*" alone on a line, the value alone on the next. */
const FIELD_NEXT_LINE = new RegExp(`^[ \\t]*${LABEL}[ \\t]*[:*]?[ \\t]*\\r?\\n[ \\t]*${FIELD_VALUE}[ \\t.]*$`, "gmu");
/** "Phase: Phase 3", "Phase of Development: II" on one line (a colon is required, so "Phase 1: Contextual Inquiry" is not a field). */
const FIELD_SAME_LINE = new RegExp(`^[ \\t]*${LABEL}[ \\t]*\\*?[ \\t]*:[ \\t]*${FIELD_VALUE}(?![\\p{L}\\p{N}])`, "gmu");
/** "Phase of Development Phase 3" (a synopsis table row flattened to one line). */
const FIELD_OF_DEVELOPMENT = new RegExp(`${PHASE_WORD}\\s+${caseless("of\\s+(?:clinical\\s+)?development")}[ \\t]*[:*]?[ \\t]*${FIELD_VALUE}(?![\\p{L}\\p{N}])`, "gu");

/** One numeral to its Arabic form with a lower-case sub-phase letter: "IIb" -> "2b", "1B" -> "1b". */
function numeral(raw: string): string {
  const m = /^(iv|iii|ii|i|[0-4])([ab])?$/i.exec(raw.trim());
  if (!m) return raw.trim();
  return `${ROMAN[m[1].toUpperCase()] ?? m[1]}${m[2] ? m[2].toLowerCase() : ""}`;
}

/** A phase value as written ("I/II", "2b", "IIb", "1/Phase 2") in its one canonical form ("Phase 1/2", "Phase 2b"). */
function canonical(value: string): string {
  const parts = value.split(/\s*[/-]\s*/).map((p) => numeral(p.replace(/^phase[ \t-]*/i, "")));
  return `Phase ${parts.join("/")}`;
}

/**
 * Any stored phase string as the canonical trial phase, "Not applicable", or
 * null when it is not a trial phase at all ("protocol_development", "Pilot").
 */
export function normaliseTrialPhase(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s) return null;
  if (/^(?:n\/?a|not\s+applicable)\.?$/i.test(s)) return NOT_APPLICABLE_PHASE;
  if (/^early\s+phase[ \t-]*(?:1|i)\.?$/i.test(s)) return EARLY_PHASE_1;
  const m = /^(?:phase[ \t-]*)?((?:iv|iii|ii|i|[0-4])[ab]?(?:\s*[/-]\s*(?:phase[ \t-]*)?(?:iv|iii|ii|i|[0-4])[ab]?)?)\s*\.?$/i.exec(s);
  if (m) return canonical(m[1]);
  // A short value with words around ONE phase: "Phase 2, randomized", "Phase III
  // (pivotal)", "Pilot Phase 2" (round 5). Two different phases, or a long
  // sentence, are not one phase.
  if (s.length > 60) return null;
  const found = new Set<string>();
  for (const t of s.matchAll(/(?<![\p{L}\p{N}])phase[ \t-]*((?:iv|iii|ii|i|[0-4])[ab]?(?:\s*[/-]\s*(?:phase[ \t-]*)?(?:iv|iii|ii|i|[0-4])[ab]?)?)(?![\p{L}\p{N}])(?!\.\d)/giu)) {
    found.add(canonical(t[1]));
  }
  return found.size === 1 ? [...found][0] : null;
}

function quoteAround(text: string, index: number, length: number): string {
  const start = Math.max(0, index - 80);
  const end = Math.min(text.length, index + length + 80);
  const s = text.slice(start, end).replace(/\s+/g, " ").trim();
  return `${start > 0 ? "..." : ""}${s}${end < text.length ? "..." : ""}`;
}

export interface TrialPhaseReading {
  /** The one stated phase, when the protocol states exactly one. */
  phase?: OperationsFact<string>;
  /** Two or more DIFFERENT stated phases: each, with its sentence. Never resolved by picking one. */
  conflict?: Array<OperationsFact<string>>;
}

/**
 * The trial phase the protocol text states, from fields first and statements
 * second. An empty result means the text states no trial phase (which a
 * reader may treat as such only when it knows the text was read).
 */
export function readTrialPhase(rawText: string): TrialPhaseReading {
  const text = typeof rawText === "string" ? rawText.replace(/\r\n?/g, "\n") : "";
  const fields: Array<OperationsFact<string>> = [];
  for (const re of [FIELD_NEXT_LINE, FIELD_SAME_LINE, FIELD_OF_DEVELOPMENT]) {
    re.lastIndex = 0;
    for (let m = re.exec(text); m; m = re.exec(text)) {
      const value = m[3] ? EARLY_PHASE_1 : m[2] ? NOT_APPLICABLE_PHASE : canonical(m[1]);
      fields.push({ value, evidence: quoteAround(text, m.index, m[0].length) });
    }
  }
  const pick = (found: Array<OperationsFact<string>>): TrialPhaseReading | null => {
    const distinct = [...new Map(found.map((f) => [f.value, f])).values()];
    if (distinct.length === 1) return { phase: distinct[0] };
    if (distinct.length > 1) return { conflict: distinct };
    return null;
  };
  const fromFields = pick(fields);
  if (fromFields) return fromFields;

  const statements: Array<OperationsFact<string>> = [];
  STATEMENT.lastIndex = 0;
  for (let m = STATEMENT.exec(text); m; m = STATEMENT.exec(text)) {
    const before = text.slice(Math.max(0, m.index - 200), m.index);
    const isThisStudy = THIS_STUDY.test(before);
    if (m.index >= TITLE_REGION_CHARS && !isThisStudy) continue;
    // "this Phase 2 study" is this study whatever was cited earlier in the
    // sentence ("Unlike the previous Phase 3 study, this Phase 2 study ...").
    if (!isThisStudy && CITED.test(sentenceBefore(text, m.index))) continue;
    statements.push({ value: canonical(m[1]), evidence: quoteAround(text, m.index, m[0].length) });
  }
  return pick(statements) ?? {};
}

// ── The study profile's phase ──────────────────────────────────────────────

type Rec = Record<string, unknown>;
const obj = (v: unknown): Rec => (v && typeof v === "object" && !Array.isArray(v) ? (v as Rec) : {});
const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);
const isTrialPhase = (v: string | null): v is string => !!v && v !== NOT_APPLICABLE_PHASE;

/**
 * A phase field in evidence text, whose line breaks quoteAround has flattened
 * ("Phase*\nPhase I" reads "Phase* Phase I"): the label, then a colon or an
 * asterisk, or the word Phase again, then the value. "Phase 1 representing"
 * is not one. Group 1: the label as written; group 2: the value as written;
 * groups 3-5: FIELD_VALUE's phase, not applicable and early phase 1.
 */
const FLAT_FIELD = new RegExp(`(?<![\\p{L}\\p{N}])(${LABEL})[ \\t]*(?:[:*][ \\t]*:?[ \\t]*|[ \\t]+(?=${PHASE_WORD}))(${FIELD_VALUE})(?![\\p{L}\\p{N}])`, "gu");

/**
 * How a review note says what the protocol states (backlog h4, round 2): the
 * phase field and its value, or the statement itself, NEVER the text around
 * them. Penn's note used to quote a reference-list fragment ("of stress and
 * health. Neurosci. Biobehav. Rev. 36, ... Study Design Phase* Phase I Design
 * As detailed above ..."); it now reads 'the protocol's Phase field reads
 * "Phase I" (Phase 1)'. When the evidence holds neither form, the phase alone.
 */
/** A stored phase fact, or undefined when it is not an object with a non-empty text value. */
function asPhaseFact(v: unknown): OperationsFact<string> | undefined {
  if (!v || typeof v !== "object" || Array.isArray(v)) return undefined;
  const f = v as { value?: unknown; evidence?: unknown };
  if (typeof f.value !== "string" || !f.value.trim()) return undefined;
  return { value: f.value, evidence: typeof f.evidence === "string" ? f.evidence : "" };
}

function said(f: OperationsFact<string>): string {
  const text = String(f.evidence ?? "").replace(/^\.\.\.|\.\.\.$/g, "").replace(/\s+/g, " ").trim();
  FLAT_FIELD.lastIndex = 0;
  for (let m = FLAT_FIELD.exec(text); m; m = FLAT_FIELD.exec(text)) {
    const value = m[5] ? EARLY_PHASE_1 : m[4] ? NOT_APPLICABLE_PHASE : m[3] ? canonical(m[3]) : null;
    // The value as written, then its canonical form when that differs: "Phase I" (Phase 1).
    const written = m[2].trim();
    if (value === f.value) return `the protocol's ${m[1].trim()} field reads "${written}"${written === value ? "" : ` (${value})`}`;
  }
  STATEMENT.lastIndex = 0;
  for (let m = STATEMENT.exec(text); m; m = STATEMENT.exec(text)) {
    if (canonical(m[1]) === f.value) return `the protocol describes the study as "${m[0].trim()}"`;
  }
  return `the protocol states the phase as "${f.value}"`;
}

export interface PhaseInputs {
  /** studyDesign/config.phase: Study Design wins, as for every fact. */
  studyDesignPhase: unknown;
  /** The protocol analysis (its studyDesign.phase is the model's reading; operationsFacts the text's). */
  analysis: unknown;
  /** projects/{id}.phase: usually a lifecycle stage ("protocol_development"), used only when it is a trial phase. */
  projectPhase: unknown;
  /** The resolved product type (productFraming.ts resolveProductType). */
  productType: ProductType | null;
}

export interface PhaseFact {
  value: string | null;
  source: FactSource | null;
  evidence?: string;
  needed?: string;
  review?: string;
}

const NEEDED = "Study phase";
const CONFIRM = "Confirm the study phase in Study Design.";

/**
 * THE ONE ANSWER to "what is this study's trial phase?" (backlog e4).
 *
 *   Study Design's phase                        wins, as for every fact
 *   product type other (no drug, no device)     Not applicable; a phase anyone
 *                                               stated is quoted in the review note
 *   the protocol text states one phase          that phase; an analysis that says
 *                                               otherwise is quoted in the note
 *   the protocol text states two phases         TBD, naming both
 *   text read, no phase stated: a device study  Not applicable (device studies
 *                                               have no development phase)
 *   text read, no phase stated: otherwise       the analysis's phase, with a note
 *                                               that the text does not state it
 *   text not read (analysed before version 4)   the analysis's phase, as before
 *   nothing                                     the project's phase only when it
 *                                               is a trial phase, else TBD
 */
export function resolvePhaseFact(inputs: PhaseInputs): PhaseFact {
  const fromDesign = str(inputs.studyDesignPhase);
  if (fromDesign && !PHASE_PLACEHOLDER.test(fromDesign)) return { value: fromDesign, source: "studyDesign" };

  const a = obj(inputs.analysis);
  const modelRaw = str(obj(a.studyDesign).phase);
  const model = normaliseTrialPhase(modelRaw);
  const ops = obj(a.operationsFacts);
  const textRead = typeof ops.version === "number" && ops.version >= 4;
  // Stored facts are guarded, not trusted (backlog h4, round 3): an entry that
  // is not an object with a text value (a null, a bare string) is skipped, so
  // malformed data never makes the resolver throw. A conflict list left with
  // one distinct phase is that one stated phase.
  const statedRaw = textRead ? asPhaseFact(ops.trialPhase) : undefined;
  const conflictAll = textRead && Array.isArray(ops.trialPhaseConflict)
    ? [...new Map((ops.trialPhaseConflict as unknown[]).map(asPhaseFact).filter((f): f is OperationsFact<string> => !!f).map((f) => [f.value, f])).values()]
    : [];
  const stated = statedRaw ?? (conflictAll.length === 1 ? conflictAll[0] : undefined);
  const conflict = conflictAll.length > 1 ? conflictAll : [];

  if (inputs.productType === "other") {
    const claims = stated && isTrialPhase(stated.value)
      ? said(stated)
      : conflict.length
        ? conflict.map(said).join(" and ")
        : isTrialPhase(model)
          ? `the protocol analysis recorded the phase as "${modelRaw}"`
          : null;
    return {
      value: NOT_APPLICABLE_PHASE,
      source: "derived",
      evidence: "No investigational drug or device (product type: other)",
      ...(claims ? { review: `Study phase recorded as not applicable: ${claims}, but the study has no investigational drug or device, and trial phases 1 to 4 describe drug and biologic development. ${CONFIRM}` } : {}),
    };
  }

  if (stated) {
    const disagrees = model !== null && model !== stated.value;
    return {
      value: stated.value,
      source: "protocol",
      evidence: stated.evidence,
      ...(disagrees ? { review: `Study phase taken from the protocol text (${said(stated)}); the protocol analysis recorded "${modelRaw}". ${CONFIRM}` } : {}),
    };
  }
  if (conflict.length) {
    return { value: null, source: null, needed: `Study phase: the protocol states more than one phase (${conflict.map(said).join("; ")}). ${CONFIRM}` };
  }
  if (textRead && inputs.productType === "device") {
    return {
      value: NOT_APPLICABLE_PHASE,
      source: "derived",
      evidence: "Medical device study; the protocol states no trial phase",
      ...(isTrialPhase(model) ? { review: `Study phase recorded as not applicable: the protocol analysis recorded "${modelRaw}", but the protocol text does not state a trial phase, and a device study has no drug development phase. ${CONFIRM}` } : {}),
    };
  }
  // The model's value in its one written form ("Phase I" -> "Phase 1", "N/A" ->
  // "Not applicable"); a placeholder or a non-phase ("Not specified.", "Pilot") is unknown.
  if (model) {
    return {
      value: model,
      source: "protocol",
      ...(textRead && isTrialPhase(model) ? { review: `The protocol analysis recorded the phase as "${modelRaw}", but the protocol text does not state it as a trial phase. ${CONFIRM}` } : {}),
    };
  }
  const project = normaliseTrialPhase(inputs.projectPhase);
  if (project) return { value: project, source: "derived" };
  return { value: null, source: null, needed: NEEDED };
}
